import { CSSProperties } from 'react';
import { getCdnUrl, transImageSrc } from 'base/utils/trans-path';
import {
    IParallaxStatus,
    IMediaCanvas,
    IStatusCanvas,
    DEFAULT_FRAME_DISTANCE,
    DEFAULT_VIDEO_STAY,
    IStatusContent,
} from './interface';
import { sum } from 'base/utils/arr';
import { IFeatureParallax } from 'mod/featureV2/interface/parallax';
import { getModuleElementId } from 'mod/featureV2/utils';
import { isVideoUrl } from 'base/utils/file';

export function validFeature(feature: IFeatureParallax): IFeatureParallax {
    return {
        ...feature,
        config: {
            ...feature.config,
            medias: feature.config.medias.filter(media => {
                if (media.type === 'canvas') {
                    if (typeof media.imageDir !== 'string' || !media.imageDir || typeof media.imageFrom !== 'number' || typeof media.imageEnd !== 'number') {
                        return false
                    }
                } else {
                    if (typeof media.src !== 'string' || !isVideoUrl(media.src)) {
                        console.log(media)
                        return false
                    }
                }
                return true;
            })
        }
    }
}

export function getCoverStyles(cover: string | undefined, medias: IFeatureParallax['config']['medias']): CSSProperties | undefined {
    if (cover) {
        return {
            backgroundImage: transImageSrc(cover),
        }
    }
    const canvas = medias.find(item => item.type === 'canvas') as IMediaCanvas;
    if (canvas) {
        return {
            backgroundImage: `url("${transImageSrc(`${canvas.imageDir}${canvas.imageFrom}.${canvas.imageFormat || 'png'}`)}")`,
            backgroundPositionX: canvas.imagePositionX || 'center'
        }
    }
}

export function getFramesHeight(frames: number, frameDistance = DEFAULT_FRAME_DISTANCE): number {
    return frames * frameDistance;
}

export function getStatusCanvasImages(media: IMediaCanvas): IStatusCanvas['images'] {
    const { imageFrom, imageEnd, extendFrames = {} } = media;
    const images: IStatusCanvas['images'] = [];
    for (let i = 0; i <= imageEnd - imageFrom; i++) {
        if (extendFrames[i] && extendFrames[i] > 0) {
            images.push(...new Array(extendFrames[i] + 1).fill({
                unextendedIndex: i,
                image: null,
            }));
        } else {
            images.push({
                unextendedIndex: i,
                image: null
            });
        }
    }
    return images;
}

export function getStatusMediaList(props: IFeatureParallax, parallax: HTMLDivElement): IParallaxStatus['medias'] {
    const { config: { medias, framesBeforeStart = 0 } } = props;
    let nextStart = framesBeforeStart;
    const elems = parallax.querySelectorAll('.g-child-media');
    return medias.map((media, index) => {
        const start = nextStart;
        let stay: number;
        if (media.type === 'canvas') {
            stay = media.imageEnd - media.imageFrom + 1;
            const extendTotal = !media.extendFrames ? 0 : sum(Object.values(media.extendFrames));
            nextStart = start + stay + extendTotal;
            return {
                ...media,
                mediaIndex: index,
                frameScrollDistance: media.frameScrollDistance || DEFAULT_FRAME_DISTANCE,
                extendFrames: media.extendFrames || {},
                elem: elems[index] as HTMLCanvasElement,
                start,
                end: nextStart - 1,
                height: getFramesHeight(stay + extendTotal, media.frameScrollDistance),
                images: getStatusCanvasImages(media),
                loaded: false,
            }
        } else {
            stay = media.stay || DEFAULT_VIDEO_STAY;
            nextStart = start + stay;
            return {
                ...media,
                mediaIndex: index,
                stay,
                elem: elems[index] as HTMLVideoElement,
                start,
                end: nextStart - 1,
                height: getFramesHeight(stay),
                loaded: false,
            }
        }
    });
}

export function getFrameTop(start: number, framesBeforeStart: number, medias: IParallaxStatus['medias']) {
    if (start === 0 || start < framesBeforeStart) {
        return start * DEFAULT_FRAME_DISTANCE;
    }
    let sumIndex = framesBeforeStart;
    let top = framesBeforeStart * DEFAULT_FRAME_DISTANCE;
    let overMedia = true;
    for (const media of medias) {
        if (start > sumIndex + (media.end - media.start)) {
            // sumIndex 大于 media显示区间
            sumIndex += (media.end - media.start + 1);
            top += media.height;
        } else {
            // sumIndex 在 media显示区间内
            if (media.type === 'video') {
                top += (start - sumIndex) * DEFAULT_FRAME_DISTANCE;
            } else {
                top += (start - sumIndex) * media.frameScrollDistance;
            }
            overMedia = false;
            break;
        }
    }
    // 超出最后一个media
    if (overMedia) {
        top += (start - sumIndex) * DEFAULT_FRAME_DISTANCE;
    }
    return top;
}

export function getStatusContentList(props: IFeatureParallax, parallax: HTMLDivElement, medias: IParallaxStatus['medias']): Array<IStatusContent> {
    const { list = [] } = props;
    const stickyElems = parallax.querySelectorAll('.g-child-text');
    const scrollElems = parallax.querySelectorAll('.g-scroll-content');

    let stickyIdx = 0;
    let srollIdx = 0;

    return list.map((item, i) => {
        const { text, ...rest } = item;
        let elem: HTMLDivElement;
        if (item.fixed === false) {
            elem = scrollElems[srollIdx] as HTMLDivElement;
            srollIdx++;
        } else {
            elem = stickyElems[stickyIdx] as HTMLDivElement;
            stickyIdx++;
        }

        return {
            ...rest,
            elem,
            startTop: typeof rest.start === 'undefined' ? 0 : getFrameTop(rest.start, props.config.framesBeforeStart || 0, medias),
            endTop: 'end' in rest ? getFrameTop(rest.end as number, props.config.framesBeforeStart || 0, medias) : 0,
        } as IStatusContent;
    })
}

export function getFramesLen(props: IFeatureParallax): number {
    const { config: { medias, framesBeforeStart = 0 } } = props;
    let len = framesBeforeStart;
    medias.map((media, index) => {
        let stay: number;
        if (media.type === 'canvas') {
            stay = media.imageEnd - media.imageFrom + 1;
            const extendTotal = !media.extendFrames ? 0 : sum(Object.values(media.extendFrames));
            len += stay + extendTotal;
        } else {
            stay = media.stay || DEFAULT_VIDEO_STAY;
            len += stay;
        }
    });
    return len;
}

export function loadImages({ srcList, onLoad }: {
    srcList: string[];
    onLoad: (index: number, image: HTMLImageElement) => void;
}) {
    const loadImage = (i: number) => {
        const image = new Image();
        image.src = getCdnUrl(srcList[i]);
        if (image.complete) {
            onLoad(i, image);
        } else {
            image.onload = () => {
                onLoad(i, image);
            }
        }
    };

    for (let i = 0; i < srcList.length; i = i + 2) {
        loadImage(i);
    }
    for (let i = 1; i < srcList.length; i = i + 2) {
        loadImage(i);
    }
}

export function getCurIndex(props: IFeatureParallax, status: IParallaxStatus) {
    const { config: { framesBeforeStart = 0 } } = props;
    const { parallax, medias } = status;
    const top = 0 - parallax.getBoundingClientRect().top;

    let curIndex = Math.round(top / DEFAULT_FRAME_DISTANCE);
    if (curIndex === 0 || curIndex < framesBeforeStart) {
        return curIndex;
    }
    curIndex = framesBeforeStart - 1;
    let totalHeight = framesBeforeStart * DEFAULT_FRAME_DISTANCE;
    let overMedia = true;
    for (const media of medias) {
        if (totalHeight + media.height <= top) {
            // top 大于 media显示区间
            curIndex += (media.end - media.start + 1);
            totalHeight += media.height;
        } else {
            // top 在 media显示区间内
            if (media.type === 'video') {
                curIndex += Math.round((top - totalHeight) / DEFAULT_FRAME_DISTANCE);
            } else {
                curIndex += Math.round((top - totalHeight) / media.frameScrollDistance);
            }
            totalHeight = top
            overMedia = false;
            break;
        }
    }
    // 超出最后一个media
    if (overMedia) {
        curIndex += Math.round((top - totalHeight) / DEFAULT_FRAME_DISTANCE);
    }
    return curIndex;
}

export function getParallaxTop(parallax) {
    return 0 - parallax.getBoundingClientRect().top;
}

export function consoleProps(props: IFeatureParallax) {
    console.log(`----- ${getModuleElementId(props.moduleId)} -----`)
    console.log(JSON.stringify(props, null, 4));
}

export function mergeDebugProps(props: IFeatureParallax, debugProps) {
    const { contentList = [], ...rest } = debugProps;
    return {
        ...rest,
        contentList: contentList.map((item, index) => ({
            ...item,
            text: props.list?.[index]?.text
        }))
    }
}

export function getMergedProps(props: IFeatureParallax) {
    try {
        let storedProps = window.localStorage.getItem(props.moduleId || '');
        if (storedProps) {
            const parsed = JSON.parse(storedProps);
            return mergeDebugProps(props, parsed)
        }
    } catch (e) {
    }
    return props;
}

export function getPositionXPercent(str?: string) {
    if (str) {
        if (str.endsWith('%')) {
            return str;
        }
        if (str === 'left') {
            return '0%';
        }
        if (str === 'right') {
            return '100%';
        }
    }
    return '50%';
}